import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Alert,
  Spin,
  Badge,
  Steps,
  Drawer
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  ReloadOutlined,
  SyncOutlined,
  HistoryOutlined,
  CheckCircleOutlined,
  DatabaseOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { useResponsive } from '../hooks/useResponsive';
import { useSimpleStore } from '../stores/simpleStore';
import { useCompanyScope, useResourcePermissions } from '../hooks/usePermissions';
import { apiGetWithScope, apiPostWithScope, apiPutWithScope, apiDelete, getApiUrl } from '../utils/api';
import { PageLayout } from './common';

const { Title } = Typography;
const { Option } = Select;
const { Step } = Steps;

interface CityCode {
  citycode: string;
  cityname: string;
}

interface PublicRoute {
  routeid: string;
  routeno: string;
  routetp: string;
  startnodenm: string;
  endnodenm: string;
  startstationcity: string;
  endstationcity: string;
}

interface Company {
  id: number;
  name: string;
  code: string;
  business_number?: string;
  address?: string;
  phone?: string;
  email?: string;
  contact_person?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface CompanyRoute {
  id: number;
  route_number: string;
  route_name: string;
  city_code: string;
  city_name: string;
  public_route_id?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  last_sync?: string;
  current_version?: number;
  version_count?: number;
}

interface RouteVersion {
  id: number;
  route_id?: number;
  version: string;
  total_stops?: number;
  is_active: boolean;
  created_at: string;
}

export default function RouteManagement() {
  const { user, selectedCompanyId: storeCompanyId, selectedCompanyName } = useSimpleStore();
  const { isMobile } = useResponsive();
  const { scope, getCompanyFilter, hasGlobalAccess, currentCompanyId } = useCompanyScope();
  const { canCreate, canUpdate, canDelete } = useResourcePermissions();
  
  // 디버깅 정보 출력
  console.log('RouteManagement 컴포넌트 마운트됨');
  console.log('useSimpleStore user:', user);
  console.log('localStorage adminToken:', localStorage.getItem('adminToken'));
  
  // 임시 토큰 설정 가이드
  if (!localStorage.getItem('adminToken')) {
    console.log('🔑 토큰이 없습니다. 다음 명령어로 임시 토큰을 설정하세요:');
    console.log('localStorage.setItem("adminToken", "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6MSwidXNlcm5hbWUiOiJhZG1pbiIsImVtYWlsIjoiYWRtaW5AYnVzbmF2aWdhdGlvbi5jb20iLCJyb2xlIjoiYWRtaW4iLCJjb21wYW55X2lkIjpudWxsLCJpYXQiOjE3NTYwOTUzNDMsImV4cCI6MTc1NjE4MTc0M30.CH_tPa1EaSun6fcr-28myC1IhN3uL7QnXuqtb2DsqPI"); window.location.reload();');
  }
  
  // State
  const [cities, setCities] = useState<CityCode[]>([]);
  const [cityVersion, setCityVersion] = useState<string>('');
  const [cityUpdatedAt, setCityUpdatedAt] = useState<string>('');
  const [refreshing, setRefreshing] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const selectedCompanyId = storeCompanyId;
  const [companyRoutes, setCompanyRoutes] = useState<CompanyRoute[]>([]);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRoute, setEditingRoute] = useState<CompanyRoute | null>(null);
  const [form] = Form.useForm();

  // 버전 관리 관련 state
  const [versionDrawerVisible, setVersionDrawerVisible] = useState(false);
  const [routeVersions, setRouteVersions] = useState<RouteVersion[]>([]);
  const [versionLoading, setVersionLoading] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState<CompanyRoute | null>(null);
  
  // 새로운 노선 추가 워크플로 state
  const [routeSearchModalVisible, setRouteSearchModalVisible] = useState(false);
  const [routeSearchStep, setRouteSearchStep] = useState(0);
  const [selectedCityForAdd, setSelectedCityForAdd] = useState<string>('');
  const [routeNumberInput, setRouteNumberInput] = useState<string>('');
  const [routeSearchResults, setRouteSearchResults] = useState<PublicRoute[]>([]);
  const [routeSearchLoading, setRouteSearchLoading] = useState(false);

  // 데이터 로드
  useEffect(() => {
    // operator는 자신의 운수사만 보기 때문에 companies 로드 불필요
    if (user?.role === 'admin') {
      loadCompanies();
    }
    loadCities();
  }, [user]);

  // 운수사 선택 시 해당 운수사의 노선 로드
  useEffect(() => {
    if (selectedCompanyId) {
      loadCompanyRoutes();
    } else {
      setCompanyRoutes([]);
    }
  }, [selectedCompanyId]);

  // 운수사 목록 로드
  const loadCompanies = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      console.log('운수사 목록 로딩 시작...');
      
      if (!token) {
        message.error('로그인이 필요합니다. 다시 로그인해주세요.');
        return;
      }
      
      const response = await apiGetWithScope('/api/companies');

      console.log('companies API 응답 상태:', response.status);
      const data = await response.json();
      console.log('companies 응답:', data);
      
      if (data.success) {
        setCompanies(data.data || []);
        console.log(`${data.data?.length || 0}개 운수사 로드됨`);
        
        // 운수사 선택은 이제 메뉴에서 처리
      } else {
        console.error('운수사 목록 로드 실패:', data.error);
        message.error(data.error || '운수사 목록을 불러올 수 없습니다');
      }
    } catch (error) {
      console.error('운수사 목록 로드 실패:', error);
      message.error('서버 연결에 실패했습니다');
    }
  };

  const loadCompanyRoutes = async () => {
    if (!selectedCompanyId) {
      return;
    }

    setCompanyLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      console.log('company-routes 로딩 시작...');
      console.log('선택된 운수사 ID:', selectedCompanyId);
      
      if (!token) {
        message.error('로그인이 필요합니다. 다시 로그인해주세요.');
        return;
      }
      
      const timestamp = Date.now();
      const response = await apiGetWithScope(`/api/company-routes/list?_t=${timestamp}`, {
        companyId: selectedCompanyId
      });

      console.log('company-routes API 응답 상태:', response.status);
      const data = await response.json();
      console.log('company-routes 응답:', data);
      
      if (data.success) {
        setCompanyRoutes(data.data || []);
        console.log(`${data.data?.length || 0}개 회사 노선 로드됨`);
      } else {
        console.error('회사 노선 로드 실패:', data.error);
        message.error(data.error || '회사 노선 목록을 불러올 수 없습니다');
      }
    } catch (error) {
      console.error('회사 노선 목록 로드 실패:', error);
      message.error('서버 연결에 실패했습니다');
    } finally {
      setCompanyLoading(false);
    }
  };

  const loadCities = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      console.log('도시 목록 로드 시작 (로컬 저장소)...');
      console.log('도시 API 인증 토큰:', token ? '존재함' : '없음');

      if (!token) {
        console.warn('인증 토큰이 없어서 도시 목록을 로드하지 않습니다.');
        message.warning('로그인이 필요합니다.');
        return;
      }

      const response = await apiGetWithScope('/api/cities/list');

      console.log('도시 목록 API 응답 상태:', response.status);

      const data = await response.json();
      console.log('도시 목록 API 응답 데이터:', data);

      if (data.success) {
        setCities(data.data.cities || []);
        setCityVersion(data.data.version || '');
        setCityUpdatedAt(data.data.updated_at || '');
        console.log(`${data.data.cities?.length || 0}개 도시 로드됨 (버전: ${data.data.version})`);
      } else {
        console.error('도시 목록 로드 실패:', data.error);
        message.error(data.error || '도시 목록을 불러올 수 없습니다');
      }
    } catch (error) {
      console.error('도시 목록 로드 실패:', error);
      message.error('도시 목록 로드 중 네트워크 오류가 발생했습니다');
    }
  };

  // 도시 목록 새로고침 (data.go.kr API에서 재다운로드)
  const refreshCities = async () => {
    setRefreshing(true);
    try {
      const token = localStorage.getItem('adminToken');
      console.log('도시 목록 새로고침 시작...');

      if (!token) {
        message.error('로그인이 필요합니다.');
        return;
      }

      const response = await fetch(getApiUrl('/api/cities/refresh'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        message.success(`도시 목록이 성공적으로 업데이트되었습니다. (${data.data.count}개 도시)`);
        await loadCities(); // 새로고침 후 재로드
      } else {
        console.error('도시 목록 새로고침 실패:', data.error);
        message.error(data.error || '도시 목록 새로고침에 실패했습니다');
      }
    } catch (error) {
      console.error('도시 목록 새로고침 실패:', error);
      message.error('도시 목록 새로고침 중 네트워크 오류가 발생했습니다');
    } finally {
      setRefreshing(false);
    }
  };

  // 새로운 노선 추가
  const handleAddCompanyRoute = () => {
    setSelectedCityForAdd('');
    setRouteNumberInput('');
    setRouteSearchResults([]);
    setRouteSearchStep(0);
    setRouteSearchModalVisible(true);
  };

  const searchRoutesByNumber = async () => {
    if (!selectedCityForAdd || !routeNumberInput.trim()) {
      message.warning('도시와 노선번호를 모두 입력해주세요');
      return;
    }

    // 입력값 검증 및 가이드
    const cityName = cities.find(city => city.citycode === selectedCityForAdd)?.cityname || '선택된 도시';
    console.log(`🔍 노선 검색 시작: ${cityName}(${selectedCityForAdd}) - ${routeNumberInput.trim()}`);

    setRouteSearchLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const timestamp = Date.now();
      const response = await apiGetWithScope(
        `/api/company-routes/search?_t=${timestamp}`,
        {
          additionalParams: {
            cityCode: selectedCityForAdd,
            routeNo: routeNumberInput.trim()
          }
        }
      );

      console.log(`📡 검색 API 응답 상태: ${response.status}`);
      const data = await response.json();
      console.log(`📊 검색 응답 데이터:`, data);
      
      if (data.success) {
        const resultCount = data.data?.length || 0;
        setRouteSearchResults(data.data || []);
        setRouteSearchStep(1);
        
        if (resultCount > 0) {
          message.success(`${resultCount}개의 노선을 찾았습니다`);
        } else {
          message.info(
            `"${routeNumberInput.trim()}"번 노선을 ${cityName}에서 찾을 수 없습니다. ` +
            '다른 노선번호를 시도해보거나 정확한 번호를 입력했는지 확인해주세요.'
          );
        }
      } else {
        console.error('노선 검색 API 오류:', data.error);
        message.error(data.error || '노선 검색에 실패했습니다');
        setRouteSearchResults([]);
      }
    } catch (error: any) {
      console.error('노선 검색 네트워크 오류:', error);
      message.error(
        `노선 검색 중 오류가 발생했습니다. 네트워크 연결을 확인하고 다시 시도해주세요. (${error?.message || ''})`
      );
      setRouteSearchResults([]);
    } finally {
      setRouteSearchLoading(false);
    }
  };

  const addRouteFromSearch = async (publicRoute: PublicRoute) => {
    if (!selectedCompanyId) {
      message.error('운수사를 먼저 선택해주세요.');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const payload = {
        route_number: publicRoute.routeno,
        route_name: `${publicRoute.startnodenm} - ${publicRoute.endnodenm}`,
        city_code: selectedCityForAdd,
        public_route_id: publicRoute.routeid,
        description: `${publicRoute.routetp} 노선`,
        company_id: selectedCompanyId
      };

      // 1단계: 노선 추가
      message.loading({ content: '노선을 추가하고 있습니다...', key: 'addRoute', duration: 0 });
      
      const response = await apiPostWithScope('/api/company-routes', payload);

      const data = await response.json();
      if (data.success) {
        message.destroy('addRoute');

        // 백엔드에서 노선 등록 시 자동 동기화 완료 (중복 sync 호출 제거)
        message.success(data.message || '노선이 추가되고 자동 동기화가 완료되었습니다!');

        setRouteSearchModalVisible(false);
        loadCompanyRoutes();
      } else {
        message.destroy('addRoute');
        message.error(data.error || '노선 추가에 실패했습니다');
      }
    } catch (error) {
      message.destroy('addRoute');
      message.destroy('syncRoute');
      console.error('노선 추가 실패:', error);
      message.error('서버 오류가 발생했습니다');
    }
  };

  // 노선 삭제
  const handleDeleteCompanyRoute = async (id: number) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(getApiUrl(`/api/company-routes/${id}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        message.success('노선이 삭제되었습니다');
        loadCompanyRoutes();
      } else {
        message.error(data.error || '노선을 삭제할 수 없습니다');
      }
    } catch (error) {
      console.error('노선 삭제 실패:', error);
      message.error('서버 오류가 발생했습니다');
    }
  };

  // 직접 동기화
  const handleDirectSync = async (route: CompanyRoute) => {
    try {
      message.loading({ content: '노선 데이터 동기화 중...', key: 'sync', duration: 0 });
      
      const token = localStorage.getItem('adminToken');
      const response = await fetch(getApiUrl(`/api/company-routes/${route.id}/sync`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          cityCode: route.city_code,
          routeId: route.public_route_id || null
        })
      });

      const data = await response.json();
      if (data.success) {
        message.destroy('sync');
        message.success(`노선 데이터가 버전 ${data.data?.version || 'N/A'}으로 동기화되었습니다.`);
        loadCompanyRoutes();
      } else {
        message.destroy('sync');
        message.error(data.error || '동기화에 실패했습니다');
      }
    } catch (error) {
      message.destroy('sync');
      console.error('동기화 실패:', error);
      message.error('서버 오류가 발생했습니다');
    }
  };

  // XML 다운로드
  const handleDownloadXML = async (versionId: number, version: number) => {
    if (!selectedRoute) return;

    try {
      const token = localStorage.getItem('adminToken');
      const timestamp = Date.now();
      
      const response = await fetch(
        getApiUrl(`/api/company-routes/${selectedRoute.id}/versions/${versionId}/download?_t=${timestamp}`),
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Cache-Control': 'no-cache'
          }
        }
      );

      if (response.ok) {
        const contentDisposition = response.headers.get('content-disposition');
        let filename = `route_${selectedRoute.route_number}_v${version}.xml`;
        
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        message.success('XML 파일이 다운로드되었습니다');
      } else {
        const errorData = await response.json();
        message.error(errorData.error || 'XML 다운로드에 실패했습니다');
      }
    } catch (error) {
      console.error('XML 다운로드 실패:', error);
      message.error('XML 다운로드 중 오류가 발생했습니다');
    }
  };

  // 버전 관리
  const handleShowVersions = async (route: CompanyRoute) => {
    setSelectedRoute(route);
    setVersionLoading(true);
    setVersionDrawerVisible(true);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(getApiUrl(`/api/company-routes/${route.id}/versions`), {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();
      console.log('버전 API 응답:', data);
      if (data.success) {
        console.log('버전 데이터:', data.data);
        setRouteVersions(data.data || []);
      } else {
        message.error(data.error || '버전 정보를 불러올 수 없습니다');
      }
    } catch (error) {
      console.error('버전 정보 로드 실패:', error);
      message.error('서버 오류가 발생했습니다');
    } finally {
      setVersionLoading(false);
    }
  };

  const handleActivateVersion = async (versionId: number) => {
    if (!selectedRoute) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        getApiUrl(`/api/company-routes/${selectedRoute.id}/versions/${versionId}/activate`),
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const data = await response.json();
      if (data.success) {
        message.success('버전이 활성화되었습니다');
        handleShowVersions(selectedRoute);
        loadCompanyRoutes();
      } else {
        message.error(data.error || '버전 활성화에 실패했습니다');
      }
    } catch (error) {
      console.error('버전 활성화 실패:', error);
      message.error('서버 오류가 발생했습니다');
    }
  };

  // 통계
  const companyStats = {
    total: companyRoutes.length,
    active: companyRoutes.filter(r => r.is_active).length,
    synced: companyRoutes.filter(r => r.last_sync).length,
    unsynced: companyRoutes.filter(r => !r.last_sync).length,
  };

  // 테이블 컬럼
  const companyRouteColumns = [
    {
      title: '노선번호',
      dataIndex: 'route_number',
      key: 'route_number',
      width: isMobile ? 80 : 100,
      render: (text: string) => <Tag color="blue">{text}</Tag>
    },
    {
      title: '노선명',
      dataIndex: 'route_name',
      key: 'route_name',
      ellipsis: true,
    },
    {
      title: '도시',
      dataIndex: 'city_name',
      key: 'city_name',
      width: isMobile ? 100 : 120,
      render: (text: string) => text || '-',
    },
    {
      title: '상태',
      key: 'status',
      width: 80,
      render: (record: CompanyRoute) => (
        <Tag color={record.is_active ? 'green' : 'red'}>
          {record.is_active ? '활성' : '비활성'}
        </Tag>
      ),
    },
    {
      title: '작업',
      key: 'actions',
      width: isMobile ? 150 : 200,
      render: (record: CompanyRoute) => (
        <Space size="small" wrap>
          <Button
            type="text"
            icon={<SyncOutlined />}
            size="small"
            onClick={() => handleDirectSync(record)}
            title="노선데이터 동기화"
          />
          <Button
            type="text"
            icon={<HistoryOutlined />}
            size="small"
            onClick={() => handleShowVersions(record)}
            title="버전 관리"
            disabled={!record.current_version}
          />
          <Popconfirm
            title="노선을 삭제하시겠습니까?"
            onConfirm={() => handleDeleteCompanyRoute(record.id)}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              size="small"
              title="삭제"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const routeSearchColumns = [
    {
      title: '노선번호',
      dataIndex: 'routeno',
      key: 'routeno',
      render: (text: string) => <Tag color="blue">{text}</Tag>
    },
    {
      title: '노선타입',
      dataIndex: 'routetp',
      key: 'routetp',
    },
    {
      title: '출발지',
      dataIndex: 'startnodenm',
      key: 'startnodenm',
      ellipsis: true,
    },
    {
      title: '도착지',
      dataIndex: 'endnodenm',
      key: 'endnodenm',
      ellipsis: true,
    },
    {
      title: '작업',
      key: 'actions',
      render: (record: PublicRoute) => (
        <Button
          type="primary"
          size="small"
          onClick={() => addRouteFromSearch(record)}
        >
          추가
        </Button>
      ),
    },
  ];

  return (
    <PageLayout
      title="노선 관리 시스템"
      subtitle="공공 노선을 가져와서 운수사별 노선으로 등록 관리"
      actions={[
        {
          key: 'add',
          label: '노선 추가',
          type: 'primary',
          icon: <PlusOutlined />,
          onClick: handleAddCompanyRoute,
          disabled: !selectedCompanyId
        },
        {
          key: 'refresh',
          label: '새로고침',
          icon: <ReloadOutlined />,
          onClick: loadCompanyRoutes
        }
      ]}
    >
      {/* 액션 버튼 */}
      <div style={{ marginBottom: 24 }}>
        <Space wrap>
          <Button
            icon={<PlusOutlined />}
            onClick={handleAddCompanyRoute}
            disabled={!selectedCompanyId}
            type="primary"
          >
            {isMobile ? '추가' : '노선 추가'}
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={loadCompanyRoutes}
            loading={companyLoading}
          >
            {isMobile ? '' : '새로고침'}
          </Button>

          {/* 도시 목록 버전 정보 및 새로고침 */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '4px 12px',
            background: '#f0f0f0',
            borderRadius: '4px',
            fontSize: '13px'
          }}>
            <span style={{ marginRight: 8 }}>
              도시 목록 v{cityVersion || '로딩중'}
            </span>
            <Button
              type="text"
              size="small"
              icon={<SyncOutlined spin={refreshing} />}
              onClick={refreshCities}
              loading={refreshing}
              title="도시 목록 새로고침 (data.go.kr API에서 재다운로드)"
            >
              {!isMobile && '새로고침'}
            </Button>
          </div>
        </Space>
      </div>

      {/* 통계 카드 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="전체 노선"
              value={companyStats.total}
              suffix="개"
              valueStyle={{ fontSize: isMobile ? 16 : 20 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="활성 노선"
              value={companyStats.active}
              suffix="개"
              valueStyle={{ color: '#52c41a', fontSize: isMobile ? 16 : 20 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="동기화됨"
              value={companyStats.synced}
              suffix="개"
              valueStyle={{ color: '#1890ff', fontSize: isMobile ? 16 : 20 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="미동기화"
              value={companyStats.unsynced}
              suffix="개"
              valueStyle={{ color: '#ff4d4f', fontSize: isMobile ? 16 : 20 }}
            />
          </Card>
        </Col>
      </Row>
      
      {/* 노선 테이블 */}
      <Card>
        {!selectedCompanyId ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Alert
              message="운수사를 선택해주세요"
              description="노선을 관리하려면 먼저 좌측 메뉴에서 운수사를 선택하세요."
              type="info"
              showIcon
            />
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 16 }}>
              <Alert
                message={`선택된 운수사: ${selectedCompanyName || companies.find(c => c.id === selectedCompanyId)?.name || '알 수 없음'}`}
                type="success"
                showIcon
                closable={false}
              />
            </div>
            <Table
              columns={companyRouteColumns}
              dataSource={companyRoutes}
              rowKey="id"
              loading={companyLoading}
              pagination={{
                pageSize: isMobile ? 10 : 20,
                showSizeChanger: !isMobile,
                showQuickJumper: !isMobile,
                showTotal: (total) => `총 ${total}개 노선`,
              }}
              scroll={{ x: isMobile ? 900 : undefined }}
              size={isMobile ? 'small' : 'middle'}
            />
          </>
        )}
      </Card>

      {/* 새로운 노선 검색 및 추가 모달 */}
      <Modal
        title="새 노선 추가"
        open={routeSearchModalVisible}
        onCancel={() => setRouteSearchModalVisible(false)}
        footer={null}
        width={isMobile ? '95%' : 800}
      >
        <div style={{ marginBottom: 24 }}>
          <Steps current={routeSearchStep} size="small">
            <Step title="도시 및 노선번호 입력" />
            <Step title="검색 결과에서 선택" />
          </Steps>
        </div>

        {routeSearchStep === 0 && (
          <div>
            <Alert
              message="새 노선 검색 가이드"
              description={
                <div>
                  <p><strong>🔍 검색 방법:</strong></p>
                  <ul style={{ marginBottom: 8 }}>
                    <li>도시를 선택하고 정확한 노선번호를 입력하세요</li>
                    <li>예: 부산 100번 → '부산광역시' + '100'</li>
                    <li>부분 검색: '50' 입력 시 50, 506, 508 등 50으로 시작하는 모든 노선</li>
                  </ul>
                  <p><strong>💡 팁:</strong> 검색 결과가 없으면 다른 도시나 정확한 번호를 확인해보세요.</p>
                </div>
              }
              type="info"
              style={{ marginBottom: 24 }}
            />
            
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item label="도시 선택">
                  <Select
                    placeholder="도시를 선택하세요"
                    value={selectedCityForAdd}
                    onChange={setSelectedCityForAdd}
                    showSearch
                    filterOption={(input, option) =>
                      option?.children?.toString().toLowerCase().includes(input.toLowerCase()) ?? false
                    }
                    style={{ width: '100%' }}
                  >
                    {cities.map(city => (
                      <Option key={city.citycode} value={city.citycode}>
                        {city.cityname}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item label="노선번호">
                  <Input
                    placeholder="예: 50, 506"
                    value={routeNumberInput}
                    onChange={(e) => setRouteNumberInput(e.target.value)}
                    onPressEnter={searchRoutesByNumber}
                  />
                </Form.Item>
              </Col>
            </Row>
            
            <div style={{ textAlign: 'right' }}>
              <Button 
                type="primary" 
                onClick={searchRoutesByNumber}
                loading={routeSearchLoading}
              >
                검색
              </Button>
            </div>
          </div>
        )}

        {routeSearchStep === 1 && (
          <div>
            <Alert
              message={`${routeSearchResults.length}개의 노선을 찾았습니다`}
              description="추가할 노선을 선택하세요."
              type="success"
              style={{ marginBottom: 16 }}
            />
            
            <Table
              columns={routeSearchColumns}
              dataSource={routeSearchResults}
              rowKey="routeid"
              pagination={{ pageSize: 5 }}
              scroll={{ x: 600 }}
              size="small"
            />
            
            <div style={{ textAlign: 'left', marginTop: 16 }}>
              <Button onClick={() => setRouteSearchStep(0)}>
                다시 검색
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* 버전 관리 Drawer */}
      <Drawer
        title={
          selectedRoute ? (
            <span>
              <HistoryOutlined /> {selectedRoute.route_number} 버전 관리
            </span>
          ) : '버전 관리'
        }
        placement="right"
        width={isMobile ? '100%' : 600}
        open={versionDrawerVisible}
        onClose={() => setVersionDrawerVisible(false)}
      >
        <div style={{ marginBottom: 16 }}>
          {selectedRoute && (
            <Alert
              message={
                <div>
                  <strong>{selectedRoute.route_name}</strong>
                  <br />
                  현재 버전: v{selectedRoute.current_version || 0}
                  <br />
                  총 버전 수: {selectedRoute.version_count || 0}개
                </div>
              }
              type="info"
            />
          )}
        </div>

        <Spin spinning={versionLoading}>
          <div style={{ marginBottom: 16 }}>
            <Title level={5}>
              <DatabaseOutlined /> XML 버전 목록
            </Title>
          </div>
          
          {routeVersions.map((version) => (
            <Card
              key={version.id}
              size="small"
              style={{ marginBottom: 12 }}
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>
                    <Tag color="blue">{version.version}</Tag>
                    {version.is_active && <Tag color="green">활성</Tag>}
                  </span>
                  <span style={{ fontSize: '12px', color: '#666' }}>
                    {new Date(version.created_at).toLocaleDateString()}
                  </span>
                </div>
              }
              extra={
                <Space size="small">
                  <Button
                    size="small"
                    onClick={() => handleDownloadXML(version.id, parseInt(version.version as any) || 1)}
                    icon={<DownloadOutlined />}
                    title="XML 다운로드"
                  >
                    {isMobile ? '' : 'XML'}
                  </Button>
                  {!version.is_active && (
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => handleActivateVersion(version.id)}
                      icon={<CheckCircleOutlined />}
                    >
                      활성화
                    </Button>
                  )}
                </Space>
              }
            >
              <div>
                <p><strong>정류장 수:</strong> {version.total_stops}개</p>
                <p><strong>생성 시각:</strong> {new Date(version.created_at).toLocaleString()}</p>
              </div>
            </Card>
          ))}
          
          {routeVersions.length === 0 && !versionLoading && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <p style={{ color: '#666' }}>버전 정보가 없습니다</p>
              <p style={{ color: '#999', fontSize: '12px' }}>노선을 먼저 동기화해주세요</p>
            </div>
          )}
        </Spin>
      </Drawer>
    </PageLayout>
  );
}